import type { TierId } from './subscription-rules';
import type { SubscriptionPrice } from './subscription-prices';

// ============================================
// Stripe Product Configuration
// ============================================

// Display data only. The Stripe price ids are per-environment rows of subscription_prices
// (VOY-601), resolved with getPriceId / getPlanFromPriceId below.
export const STRIPE_PRODUCTS = {
  // Note: VOY_FREE is a virtual tier - no Stripe product
  VOY_FREE: {
    name: 'Free',
    description: 'Standard pricing, Unlimited searches, Basic support',
    price: 0,
    annualPrice: 0,
    mode: 'subscription' as const,
    tier: 'free' as TierId,
  },
  // Align product keys with tier IDs for clarity
  VOY_PRO: {
    name: 'Pro',
    description:
      'Everything in Business, plus: Cancel booked flights from your trips',
    price: 5.0,
    annualPrice: 48.0,
    mode: 'subscription' as const,
    tier: 'pro' as TierId,
  },
  VOY_BUSINESS: {
    name: 'Business',
    description:
      'Unlimited bookings',
    price: 7.0,
    annualPrice: 70.0,
    mode: 'subscription' as const,
    tier: 'business' as TierId,
  },
  VOY_FAMILY: {
    name: 'Family',
    description:
      'Book for family members',
    price: 3.0,
    annualPrice: 30.0,
    mode: 'subscription' as const,
    tier: 'family' as TierId,
  },
} as const;

export type StripePlan = keyof typeof STRIPE_PRODUCTS;

// Tier mapping using Stripe products as source of truth
export const STRIPE_PLAN_TO_TIER: Record<StripePlan, TierId> = {
  VOY_FREE: 'free',
  VOY_PRO: STRIPE_PRODUCTS.VOY_PRO.tier,
  VOY_BUSINESS: STRIPE_PRODUCTS.VOY_BUSINESS.tier,
  VOY_FAMILY: STRIPE_PRODUCTS.VOY_FAMILY.tier,
} as const;

// Get all paid plans (excluding free)
export const PAID_PLANS: StripePlan[] = ['VOY_PRO', 'VOY_BUSINESS', 'VOY_FAMILY'];

// ============================================
// Type Definitions
// ============================================

export interface PlanFromPriceId {
  plan: StripePlan;
  tier: TierId;
  isAnnual: boolean;
}

// ============================================
// Helper Functions
// ============================================

/**
 * Get plan, tier, and billing interval from a Stripe price ID, for the subscription page's catalog
 * (which product card is current, monthly vs annual). VOY-554: NOT for a user's pricing tier; that
 * comes from subscription_prices (UserSubscription.tier_id via getTierFromSubscription).
 * @param priceId - The Stripe price ID
 * @param prices - subscription_prices rows (loadSubscriptionPrices / useSubscriptionPrices)
 * @returns Object with plan key, tier, and whether it's annual, or null if not found
 */
export function getPlanFromPriceId(
  priceId: string | null | undefined,
  prices: readonly SubscriptionPrice[]
): PlanFromPriceId | null {
  if (!priceId) return null;

  // Any row, active or not: an existing subscription may be on a retired price.
  const row = prices.find((p) => p.stripe_price_id === priceId);
  if (!row) return null;

  const entry = getProductByTier(row.tier_id as TierId);
  if (!entry) return null;

  return {
    plan: entry.plan,
    tier: entry.product.tier,
    isAnnual: row.interval === 'year',
  };
}

/**
 * Get the Stripe product by plan key
 */
export function getPlanDetails(plan: StripePlan) {
  return STRIPE_PRODUCTS[plan];
}

/**
 * Get the Stripe product by tier ID
 */
export function getProductByTier(tier: TierId) {
  const entry = Object.entries(STRIPE_PRODUCTS).find(([_, product]) => product.tier === tier);
  return entry ? { plan: entry[0] as StripePlan, product: entry[1] } : null;
}

/**
 * Extract benefits from plan description as an array
 * Useful for displaying benefits in a structured way (e.g., in subscription card)
 * Handles "Everything in X, plus:" prefix by filtering it out
 * 
 * @param plan - The Stripe plan key
 * @returns Array of benefit strings (without "Everything in X, plus:" prefix)
 */
export function getPlanBenefits(plan: StripePlan): string[] {
  const product = STRIPE_PRODUCTS[plan];
  if (!product) return [];
  
  // Split by comma and clean up
  const benefits = product.description
    .split(',')
    .map(benefit => benefit.trim())
    .filter(benefit => benefit.length > 0);
  
  // Filter out "Everything in X, plus:" prefix if present
  // Also filter out standalone "plus:" at the start of benefits
  return benefits
    .filter(benefit => {
      const lower = benefit.toLowerCase().trim();
      // Skip "Everything in X" lines
      if (lower.startsWith('everything in')) return false;
      // Skip lines that end with "plus:" (the separator, e.g., "Starter, plus:")
      // But keep lines that have content after "plus:" (e.g., "plus: Unlimited rewards")
      if (lower === 'plus:' || lower.endsWith(' plus:')) return false;
      return true;
    })
    .map(benefit => {
      // Clean up "plus:" prefix but keep the rest of the benefit
      // This handles cases like "plus: Unlimited rewards" -> "Unlimited rewards"
      return benefit.trim().replace(/^plus:\s*/i, '').trim();
    })
    .filter(benefit => benefit.length > 0);
}

/**
 * Get plan display name with friendly formatting
 * 
 * @param plan - The Stripe plan key
 * @returns Formatted plan name
 */
export function getPlanDisplayName(plan: StripePlan): string {
  return STRIPE_PRODUCTS[plan]?.name || plan;
}

/**
 * Get plan display name by tier
 * 
 * @param tier - The tier ID
 * @returns Formatted plan name
 */
export function getTierDisplayName(tier: TierId): string {
  const productEntry = getProductByTier(tier);
  return productEntry?.product.name || tier.charAt(0).toUpperCase() + tier.slice(1);
}

/**
 * Get the price ID to sell for a plan (handles annual vs monthly): the single active `standard`
 * subscription_prices row of its tier and interval. Null for Free, when there is none, or when
 * more than one matches (each environment keeps active rows of ONE stripe_mode only, see
 * subscription-prices); the caller must not check out without an id.
 */
export function getPriceId(
  plan: StripePlan,
  isAnnual: boolean,
  prices: readonly SubscriptionPrice[]
): string | null {
  const product = STRIPE_PRODUCTS[plan];
  if (!product || product.tier === 'free') return null;

  const interval = isAnnual ? 'year' : 'month';
  const matches = prices.filter(
    (p) => p.active && p.kind === 'standard' && p.tier_id === product.tier && p.interval === interval
  );
  if (matches.length > 1) {
    console.error('[stripe-config] More than one active standard price; selling none', {
      plan,
      interval,
      priceIds: matches.map((p) => p.stripe_price_id),
    });
    return null;
  }
  return matches[0]?.stripe_price_id ?? null;
}
